import { v4 as uuidv4 } from 'uuid';

import { EKycStatus, type IUserDetails } from '../../storage/store';

/**
 * Survives the round trip to DigiLocker, which leaves the origin entirely.
 *
 * `localStorage`, not `sessionStorage`: DigiLocker routinely hands off to a
 * custom tab or the DigiLocker app, and a return in a new tab sees an empty
 * `sessionStorage`. Losing this key is not recoverable — the backend registers
 * no SurePass webhook, so completing from the client is the *only* path by which
 * a verification is ever finished. A lost `clientId` means the user authenticated
 * at DigiLocker for nothing.
 */
export const KYC_PENDING_KEY = 'kyc-digilocker-pending';

/** Used when the init response omits `expirySeconds`. */
const DEFAULT_TTL_MS = 15 * 60 * 1000;

/**
 * Tags a message as coming from our own callback page rather than from any
 * other frame that happens to hold a handle on the app.
 */
export const KYC_POPUP_MESSAGE_SOURCE = 'zupee-kyc-popup';

/**
 * Where `kyc-callback.html` looks for the nonce. Read by a plain static page,
 * so this string is duplicated there — keep the two in step.
 */
export const KYC_POPUP_NONCE_KEY = 'kyc-digilocker-popup-nonce';

/** What `kyc-callback.html` posts back to the opener. */
export interface IKycPopupMessage {
  source: typeof KYC_POPUP_MESSAGE_SOURCE;
  /** Ties the message to the popup the current attempt opened. */
  nonce: string;
  clientId: string;
  /** Whatever DigiLocker put on the return URL; advisory only. */
  status?: string | null;
}

/**
 * Opens the channel the popup reports back on, returning the nonce that
 * identifies this attempt.
 *
 * The nonce matters because the pending session outlives a single attempt: a
 * user can abandon a popup, open another, and have the first one report late.
 * Without it the stale message would resolve the newer attempt.
 *
 * `localStorage` rather than a variable, because the callback page runs in a
 * different window and can only reach us through shared origin storage.
 */
export function armKycPopupBridge(): string {
  const nonce = uuidv4();
  try {
    localStorage.setItem(KYC_POPUP_NONCE_KEY, nonce);
  } catch {
    // Private mode or quota. The popup then falls back to redirecting itself,
    // which the pending session still completes.
  }
  return nonce;
}

export function disarmKycPopupBridge() {
  try {
    localStorage.removeItem(KYC_POPUP_NONCE_KEY);
  } catch {
    // Nothing to do — a stale nonce is only ever compared, never trusted.
  }
}

/**
 * Adds `origin`, which is what makes the hosted SurePass page post its result to
 * the opener instead of navigating itself to the configured redirect URL.
 *
 * Harmless if the vendor ignores it: the redirect then lands on
 * `kyc-callback.html`, which messages the opener itself.
 */
export function withPopupOrigin(url: string): string {
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}origin=${encodeURIComponent(window.location.origin)}`;
}

export interface IKycPendingSession {
  clientId: string;
  docType: number;
  /**
   * The hosted DigiLocker URL this session was started with.
   *
   * Parked so a second attempt reopens the *same* session instead of paying for
   * another one — every SurePass `initialize` is billed. The token embedded in
   * the URL carries the `client_id`, so this alone is enough to resume.
   *
   * Optional: a session recovered from the return query string has no URL, and
   * that path only ever completes, never reopens.
   */
  url?: string;
  /** Where to send the user once verification resolves. */
  returnTo?: string;
  /** Whether the user was mid-withdrawal when KYC blocked them. */
  resumeWithdraw?: boolean;
  /** Epoch ms. Past this the session is dropped rather than retried forever. */
  expiresAt?: number;
  /**
   * Automatic (on-mount) completion attempts already spent.
   *
   * Bounds a session the backend will never resolve: without it, every boot
   * fires another `completeDigiLockerKyc`. A deliberate retry resets it, since a
   * user asking again is evidence the session is worth another try.
   */
  attempts?: number;
}

export function setPendingKycSession(
  session: IKycPendingSession,
  expirySeconds?: number,
) {
  const ttlMs =
    typeof expirySeconds === 'number' && expirySeconds > 0
      ? expirySeconds * 1000
      : DEFAULT_TTL_MS;

  const payload: IKycPendingSession = {
    ...session,
    expiresAt: Date.now() + ttlMs,
  };

  try {
    localStorage.setItem(KYC_PENDING_KEY, JSON.stringify(payload));
  } catch {
    // Private mode or quota. Starting the flow still works; only the return trip
    // is weakened, and the `clientId` query-param fallback covers that case.
  }
}

/**
 * Writes a session back without touching its TTL.
 *
 * Not `setPendingKycSession`, which recomputes `expiresAt` from `Date.now()` —
 * bumping the attempt count through that would extend the deadline on every
 * attempt and turn a bounded session into an immortal one.
 */
function writePendingKycSession(session: IKycPendingSession) {
  try {
    localStorage.setItem(KYC_PENDING_KEY, JSON.stringify(session));
  } catch {
    // Private mode or quota. The count is an optimisation, not a correctness
    // requirement — the `expiresAt` bound still applies.
  }
}

/** Counts one automatic completion attempt against the parked session. */
export function recordKycAttempt() {
  const session = getPendingKycSession();
  if (!session) return;
  writePendingKycSession({ ...session, attempts: (session.attempts ?? 0) + 1 });
}

/** Restores the automatic budget after a deliberate retry. */
export function resetKycAttempts() {
  const session = getPendingKycSession();
  if (!session) return;
  writePendingKycSession({ ...session, attempts: 0 });
}

function readRaw(): string | null {
  try {
    // sessionStorage is read as a fallback so a user who started the flow on the
    // previous build is not stranded mid-verification by this change.
    return (
      localStorage.getItem(KYC_PENDING_KEY) ??
      sessionStorage.getItem(KYC_PENDING_KEY)
    );
  } catch {
    return null;
  }
}

export function getPendingKycSession(): IKycPendingSession | null {
  try {
    const raw = readRaw();
    if (!raw) return null;

    const parsed = JSON.parse(raw) as IKycPendingSession;
    if (!parsed?.clientId) return null;

    if (typeof parsed.expiresAt === 'number' && Date.now() > parsed.expiresAt) {
      clearPendingKycSession();
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function clearPendingKycSession() {
  try {
    localStorage.removeItem(KYC_PENDING_KEY);
    sessionStorage.removeItem(KYC_PENDING_KEY);
  } catch {
    // Nothing to do — a stale entry expires on its own via `expiresAt`.
  }
}

export function isKycApproved(userDetails?: IUserDetails): boolean {
  return userDetails?.kycStatus?.status === EKycStatus.SUCCESS;
}

/** Pending verification is in progress — offering "verify" again would confuse. */
export function isKycPending(userDetails?: IUserDetails): boolean {
  return userDetails?.kycStatus?.status === EKycStatus.PENDING;
}
